<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
import btcIcon from '~/assets/img/exchange-detail/exchange-funds/btc.png'
import ethIcon from '~/assets/img/exchange-detail/exchange-funds/eth.png'
import type { TraderSupportCurrency, TraderSupportCurrencyPair } from '~/server/detail'
import Up from '@/assets/icon/up.svg'
import Down from '@/assets/icon/down.svg'

const { localePath, t } = useAppI18n()

type MetricItem = {
  label: string
  value: string
  reverse?: boolean
  compact?: boolean
}

type SupportedCurrencyRow = {
  code: string
  name: string
  pair: string
  price: string
  change: string
  changeColor: 'up' | 'down'
  volume: string
  depth: string
  liquidity: string
  logoSrc?: string
  logoText?: string
  logoClass: string
}

const props = defineProps<{
  exchangeName?: string
  exchangeId?: string
  exchangeSlug?: string
  supportedCurrencies?: TraderSupportCurrency | null
}>()
const matomo = useMatomo()

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const fallbackIcons: Record<string, string> = {
  BTC: btcIcon,
  ETH: ethIcon,
}

const getLogoMeta = (item: TraderSupportCurrencyPair) => {
  const code = normalizeText(item.code)
  const logoSrc = normalizeText(item.baseLogo) || fallbackIcons[code]

  if (logoSrc) {
    return {
      logoSrc,
      logoText: '',
      logoClass: 'exchange-supported-currencies-card__logo-image-wrap',
    }
  }

  return {
    logoSrc: '',
    logoText: code.slice(0, 1) || '?',
    logoClass: 'exchange-supported-currencies-card__logo-fallback',
  }
}

const supportedCurrenciesDetailPath = computed(() => {
  if (!props.exchangeSlug) {
    return ''
  }

  return localePath({
    path: `/exchanges/${encodeURIComponent(props.exchangeSlug)}/coins`,
  })
})

const cardTitle = computed(() => normalizeText(props.supportedCurrencies?.topicTitle) || '支持币种')

const metrics = computed<MetricItem[]>(() => [
  { value: normalizeText(props.supportedCurrencies?.coinCount) || '--', label: t('82661') },
  { value: normalizeText(props.supportedCurrencies?.pairCount) || '--', label: t('84049') },
  {
    label: t('82671'),
    value: normalizeText(props.supportedCurrencies?.liquidityRank) || '--',
    reverse: true,
    compact: true,
  },
])

const tableHeaders = computed(() => {
  const headers = props.supportedCurrencies?.headers || []

  if (headers.length === 7) {
    return headers
  }

  return ['币种/名称', '交易对', '单价', '涨跌幅', '24H交易量', '+2% / -2% 市场深度', '流动性']
})

const rows = computed<SupportedCurrencyRow[]>(() => {
  return (props.supportedCurrencies?.pairList || []).map((item) => {
    const logoMeta = getLogoMeta(item)

    return {
      code: normalizeText(item.code) || '--',
      name: normalizeText(item.name) || '--',
      pair: normalizeText(item.pairCount) || '--',
      price: normalizeText(item.price) || '--',
      change: normalizeText(item.change) || '--',
      changeColor: item.changeType === 1 ? 'up' : 'down',
      volume: normalizeText(item.volume24h) || '--',
      depth: normalizeText(item.marketDepth) || '--',
      liquidity: normalizeText(item.liquidity) || '--',
      ...logoMeta,
    }
  })
})

const trackSupportedCurrenciesDetail = () => {
  matomo.trackEvent('broker_column_support', {
    af_name: normalizeText(props.exchangeName),
    af_code: normalizeText(props.exchangeId),
  })
}
</script>

<template>
  <section class="exchange-supported-currencies-card" aria-labelledby="exchange-supported-currencies-title">
    <div class="exchange-supported-currencies-card__header">
      <div class="exchange-supported-currencies-card__title-row">
        <h2 id="exchange-supported-currencies-title" class="exchange-supported-currencies-card__title">
          {{ cardTitle }}
        </h2>
        <NuxtLink
          v-if="supportedCurrenciesDetailPath"
          class="exchange-supported-currencies-card__arrow"
          :to="supportedCurrenciesDetailPath"
          target="_blank"
          aria-label="查看支持币种详情"
          @click="trackSupportedCurrenciesDetail"
        >
          <ArrowRight class="action-icon" aria-hidden="true" />
        </NuxtLink>
      </div>

      <div class="exchange-supported-currencies-card__metrics" aria-label="支持币种概览">
        <div
          v-for="metric in metrics"
          :key="`${metric.label}-${metric.value}`"
          class="exchange-supported-currencies-card__metric"
          :class="{
            'exchange-supported-currencies-card__metric-reverse': metric.reverse,
            'exchange-supported-currencies-card__metric-compact': metric.compact,
          }"
        >
          <template v-if="metric.reverse">
            <span class="exchange-supported-currencies-card__metric-label">{{ metric.label }}</span>
            <span class="exchange-supported-currencies-card__metric-value">{{ metric.value }}</span>
          </template>
          <template v-else>
            <span class="exchange-supported-currencies-card__metric-value">{{ metric.value }}</span>
            <span class="exchange-supported-currencies-card__metric-label">{{ metric.label }}</span>
          </template>
        </div>
      </div>
    </div>

    <div class="exchange-supported-currencies-card__table-wrap">
      <table class="exchange-supported-currencies-card__table">
        <colgroup>
          <col class="exchange-supported-currencies-card__col-coin" />
          <col class="exchange-supported-currencies-card__col-pair" />
          <col class="exchange-supported-currencies-card__col-price" />
          <col class="exchange-supported-currencies-card__col-change" />
          <col class="exchange-supported-currencies-card__col-volume" />
          <col class="exchange-supported-currencies-card__col-depth" />
          <col class="exchange-supported-currencies-card__col-liquidity" />
        </colgroup>
        <thead>
          <tr>
            <th
              v-for="(header, index) in tableHeaders"
              :key="header"
              :class="{ 'exchange-supported-currencies-card__table-th-right': index === 6 }"
            >
              {{ header }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in rows" :key="`${item.code}-${item.name}-${item.pair}`">
            <td>
              <div class="exchange-supported-currencies-card__coin">
                <span class="exchange-supported-currencies-card__logo" :class="item.logoClass">
                  <CommonImage
                    v-if="item.logoSrc"
                    :src="item.logoSrc"
                    :alt="item.name"
                    class="exchange-supported-currencies-card__logo-image"
                  />
                  <span v-else>{{ item.logoText }}</span>
                </span>
                <span class="exchange-supported-currencies-card__coin-name">{{ item.name }}</span>
              </div>
            </td>
            <td>{{ item.pair }}</td>
            <td>{{ item.price }}</td>
            <td
              class="exchange-supported-currencies-card__change"
              :class="{
                'exchange-supported-currencies-card__change-up': item.changeColor === 'up',
                'exchange-supported-currencies-card__change-down': item.changeColor === 'down',
              }"
            >
              {{ item.change }}
              <Up
                v-if="item.changeColor === 'up'"
                class="change-icon exchange-supported-currencies-card__change-icon-up"
                aria-hidden="true"
              />
              <Down
                v-else
                class="change-icon exchange-supported-currencies-card__change-icon-down"
                aria-hidden="true"
              />
            </td>
            <td>{{ item.volume }}</td>
            <td class="exchange-supported-currencies-card__depth">{{ item.depth }}</td>
            <td class="exchange-supported-currencies-card__table-td-right">{{ item.liquidity }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped lang="less">
.exchange-supported-currencies-card {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  padding: 24px 24px 6px;
  border: 1px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #ffffff;
}

.exchange-supported-currencies-card__header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  width: 100%;
}

.exchange-supported-currencies-card__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 16px;
}

.exchange-supported-currencies-card__title {
  margin: 0;
  color: #0f1728;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.exchange-supported-currencies-card__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 2px;
  color: #7d818a;
  background: transparent;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: #333;
    outline: none;
  }
}

.action-icon {
  width: 20px;
  height: 20px;
}

.action-icon :deep(path) {
  fill: currentColor;
}

.exchange-supported-currencies-card__metrics {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.exchange-supported-currencies-card__metric {
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #0f1728;
}

.exchange-supported-currencies-card__metric-reverse {
  gap: 4px;
}

.exchange-supported-currencies-card__metric-value {
  color: #0f1728;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.exchange-supported-currencies-card__metric-compact {
  align-items: center;
  gap: 6px;
  border-radius: 2px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  padding: 4px 8px;
}

.exchange-supported-currencies-card__metric-compact .exchange-supported-currencies-card__metric-value {
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
}

.exchange-supported-currencies-card__metric-label {
  color: #505258;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.exchange-supported-currencies-card__table-wrap {
  width: 100%;
}

.exchange-supported-currencies-card__table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.exchange-supported-currencies-card__col-coin {
  width: 206px;
}

.exchange-supported-currencies-card__col-pair,
.exchange-supported-currencies-card__col-price,
.exchange-supported-currencies-card__col-change,
.exchange-supported-currencies-card__col-volume,
.exchange-supported-currencies-card__col-liquidity {
  width: 148px;
}

.exchange-supported-currencies-card__col-depth {
  width: 206px;
}

.exchange-supported-currencies-card__table thead th {
  padding: 0 32px 12px 0;
  border-bottom: 1px solid #eef2f6;
  color: #a8b0bd;
  text-align: right;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  white-space: nowrap;

  &:first-child {
    text-align: left;
  }
}

.exchange-supported-currencies-card__table tbody td {
  height: 64px;
  padding: 0 32px 0 0;
  border-bottom: 1px solid #f2f5f8;
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  line-height: 17px;
  vertical-align: middle;

  &:first-child {
    text-align: left;
  }
}

.exchange-supported-currencies-card__table thead th:last-child,
.exchange-supported-currencies-card__table tbody td:last-child {
  padding-right: 0;
}

.exchange-supported-currencies-card__table tbody tr:last-child td {
  border-bottom: none;
}

.exchange-supported-currencies-card__coin {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.exchange-supported-currencies-card__logo {
  display: inline-flex;
  width: 32px;
  height: 32px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}

.exchange-supported-currencies-card__logo-image-wrap {
  background: #ffffff;
}

.exchange-supported-currencies-card__logo-fallback {
  background: #edf2ff;
  color: #1f3f93;
}

.exchange-supported-currencies-card__logo-image {
  width: 32px;
  height: 32px;
  display: block;
}

.exchange-supported-currencies-card__coin-name {
  overflow: hidden;
  color: #1a2233;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.exchange-supported-currencies-card__change {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.change-icon {
  flex: none;
  transform-origin: center;
  will-change: transform, opacity;
}

.exchange-supported-currencies-card__change-up {
  color: #1f845a !important;
}

.exchange-supported-currencies-card__change-down {
  color: #e2483d !important;
}

.exchange-supported-currencies-card__change-icon-up {
  animation: exchange-supported-currencies-rise 1.5s ease-in-out infinite;
}

.exchange-supported-currencies-card__change-icon-down {
  animation: exchange-supported-currencies-fall 1.5s ease-in-out infinite;
}

.exchange-supported-currencies-card__depth {
  color: #202938;
}

.exchange-supported-currencies-card__table-th-right,
.exchange-supported-currencies-card__table-td-right {
  text-align: right;
}

@keyframes exchange-supported-currencies-rise {
  0%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  50% {
    transform: translateY(-3px);
    opacity: 0.82;
  }
}

@keyframes exchange-supported-currencies-fall {
  0%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }

  50% {
    transform: translateY(3px);
    opacity: 0.82;
  }
}

@media (prefers-reduced-motion: reduce) {
  .exchange-supported-currencies-card__change-icon-up,
  .exchange-supported-currencies-card__change-icon-down {
    animation: none;
  }
}
</style>
